import React from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DragIndicator } from '@mui/icons-material';
import { Box, Button, Paper, Stack } from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { ExperienceImage } from '~/components/Experiences/Home/Content/styles';

type SortableListItemProps = {
  value: Experiences.Image;
  toRemove: boolean;
  imageHero: number | string | null;
  handleSetImageHero: (imageId: number | string) => void;
  handleDeleteImage: (imageId: number | string) => void;
};

function SortableListItem({
  value,
  toRemove,
  imageHero,
  handleSetImageHero,
  handleDeleteImage,
}: SortableListItemProps) {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: value.id,
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <Paper
      variant="outlined"
      sx={{ width: '100%', maxWidth: 350, p: 1, display: 'flex', gap: 2 }}
      ref={setNodeRef}
      style={style}
    >
      <Box style={{ cursor: 'pointer' }} {...attributes} {...listeners}>
        <DragIndicator />
      </Box>
      <Stack direction="column" spacing={2}>
        <ExperienceImage
          src={value.urls[0]?.url}
          alt={`${value.description}`}
          style={{ opacity: toRemove ? 0.2 : 1 }}
        />

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          {value.isHero ? (
            <p>This is the hero image</p>
          ) : (
            <Button
              variant="text"
              size="small"
              onClick={() => handleSetImageHero(value.id)}
              disabled={imageHero === value.id || toRemove}
            >
              Set as hero
            </Button>
          )}
          <Button variant="text" size="small" onClick={() => handleDeleteImage(value.id)}>
            {toRemove ? 'Marked to delete' : 'Delete image'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

type ExperienceCurationImagesProps = {
  items: Array<Experiences.Image>;
  handleSetImageHero: (imageId: number | string) => void;
  handleDeleteImage: (imageId: number | string) => void;
  imageHero: number | string | null;
  imagesToRemove: Array<number | string>;
  onDragEnd: (event: DragEndEvent) => void;
};

function ExperienceCurationImages({
  items,
  handleSetImageHero,
  handleDeleteImage,
  imageHero,
  imagesToRemove,
  onDragEnd,
}: ExperienceCurationImagesProps) {
  return (
    <DndContext onDragEnd={onDragEnd}>
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <Box display="flex" gap={2} flexDirection="column" maxHeight={600} overflow="scroll">
          {items.map((value) => (
            <SortableListItem
              key={value.id}
              value={value}
              toRemove={imagesToRemove.includes(value.id)}
              imageHero={imageHero}
              handleSetImageHero={handleSetImageHero}
              handleDeleteImage={handleDeleteImage}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

export default ExperienceCurationImages;
